import axios from "axios";
import { API_BASE_URL } from "../../config";
import { authHeaders } from "../utils/session";

/**
 * Email widgets (2026-10-27) — company email designs. A widget's header and footer are fixed
 * company details; its {{content}} slot holds what an internal user writes. Used by Bulk Email
 * ("Custom"), by templates that pick one, and by the inboxes when such a template is chosen.
 *
 * Reading is open to any CMS user; every write is superadmin-only and answers 403 otherwise.
 */

const jsonHeaders = () => ({
  ...authHeaders(),
  "Content-Type": "application/json",
});

/** Turn an API failure into the message the user should actually read. */
const apiError = (err, fallback) => {
  const status = err?.response?.status;
  const data = err?.response?.data;
  if (status === 403) return new Error(data?.message || "Only a superadmin can manage email widgets.");
  if (status === 404) return new Error(data?.message || "Email widget not found.");
  return new Error(data?.message || data?.error || fallback);
};

const unwrap = (res, fallback) => {
  const body = res.data || {};
  if (body.status && body.status !== 200 && body.status !== true) {
    throw new Error(body.message || fallback);
  }
  return body.data ?? body;
};

const call = async (request, fallback) => {
  try {
    return unwrap(await request(), fallback);
  } catch (err) {
    throw apiError(err, fallback);
  }
};

export const fetchEmailWidgets = async () => {
  const data = await call(() => axios.get(`${API_BASE_URL}/email-widgets`, { headers: jsonHeaders() }), "Failed to load email widgets");
  return Array.isArray(data) ? data : [];
};

export const fetchEmailWidget = (id) =>
  call(() => axios.get(`${API_BASE_URL}/email-widgets/${id}`, { headers: jsonHeaders() }), "Failed to load the email widget");

export const createEmailWidget = (payload) =>
  call(() => axios.post(`${API_BASE_URL}/email-widgets`, payload, { headers: jsonHeaders() }), "Failed to save the email widget");

export const updateEmailWidget = (id, payload) =>
  call(() => axios.put(`${API_BASE_URL}/email-widgets/${id}`, payload, { headers: jsonHeaders() }), "Failed to save the email widget");

export const setDefaultEmailWidget = (id) =>
  call(() => axios.patch(`${API_BASE_URL}/email-widgets/${id}/default`, {}, { headers: jsonHeaders() }), "Failed to make it the default");

export const toggleEmailWidgetStatus = (id) =>
  call(() => axios.patch(`${API_BASE_URL}/email-widgets/${id}/toggle-status`, {}, { headers: jsonHeaders() }), "Failed to update the status");

export const deleteEmailWidget = (id) =>
  call(() => axios.delete(`${API_BASE_URL}/email-widgets/${id}`, { headers: jsonHeaders() }), "Failed to delete the email widget");
